<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-800 to-gray-900 overflow-x-hidden">
    <!-- Header -->
    <header class="w-full px-[12.5%] py-8 backdrop-blur-sm bg-gray-800/50 border-b border-gray-700">
      <div class="max-w-6xl mx-auto">
        <h1 class="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300 mb-2">Art Gallery</h1>
        <p class="text-xl text-gray-400">Abstract paintings, digital creations, and spiritual expressions</p>
      </div>
    </header>

    <!-- Main Content -->
    <main class="w-full px-[12.5%] py-12">
      <div class="max-w-6xl mx-auto">
        <!-- Category Filters -->
        <div class="mb-12 flex flex-wrap gap-3 justify-center animate-fade-in">
          <button
            v-for="category in categories"
            :key="category.id"
            @click="setActiveCategory(category.id)"
            :class="[
              'px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2',
              activeCategory === category.id
                ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white shadow-lg'
                : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border border-gray-600'
            ]"
          >
            <span v-html="category.emoji"></span>
            {{ category.name }}
          </button>
        </div>

        <!-- Gallery Grid -->
        <div v-if="filteredMedia.length > 0" class="grid md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fade-in">
          <!-- In your gallery grid item (replace the current image/video div) -->
          <div 
            v-for="item in paginatedMedia" 
            :key="item.id"
            class="group relative overflow-hidden rounded-2xl bg-gray-800/50 backdrop-blur-sm border border-gray-700 hover:border-blue-400/30 transition-all duration-500 hover:-translate-y-2"
            @click="openLightbox(item)"
          >
            <!-- Image/Video Thumbnail -->
            <div class="relative aspect-square overflow-hidden">
              <img
                v-if="item.type === 'image'"
                :src="item.thumbnail"
                :alt="item.title"
                class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <video
                v-else-if="item.type === 'video'"
                :poster="item.thumbnail"
                class="w-full h-full object-cover"
                muted
                loop
              >
                <source :src="item.src" type="video/mp4">
              </video>
              <div
                v-else
                class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-900/50 to-purple-900/50"
              >
                <div class="text-5xl">{{ item.type === 'audio' ? '🎵' : '🎤' }}</div>
              </div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <h3 class="text-white text-lg font-medium">Untitled {{ item.id[0] + item.id.replace(/\D+/g, '') }}</h3>
              </div>
              <div v-if="item.type !== 'image'" class="absolute top-3 right-3 bg-black/50 rounded-full p-2">
                {{ item.type === 'video' ? '▶️' : '🔊' }}
              </div>
            </div>

            <!-- Item Info -->
            <div class="p-4">
              <div class="flex justify-between items-center mb-2">
                <span class="text-xs text-white font-light px-2 py-1 rounded-full bg-gray-700/50">
                  {{ item.category }}
                </span>
                <span class="text-xs text-gray-400">
                  <!-- {{ formatDate(item.date) }} -->Some Day
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-16 animate-fade-in">
          <div class="text-5xl mb-4">🖼️</div>
          <h3 class="text-xl text-gray-300 mb-2">No media found</h3>
          <p class="text-gray-500 max-w-md mx-auto">Try selecting different filters or check back later for new additions</p>
        </div>

        <!-- Pagination -->
        <div v-if="filteredMedia.length > 0" class="mt-16 flex justify-center animate-fade-in">
          <nav class="flex items-center gap-2">
            <button
              v-for="page in totalPages"
              :key="page"
              @click="switchPage(page)"
              :class="[
                'px-5 py-2 rounded-full text-sm font-medium transition-all',
                currentPage === page
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white shadow-md'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border border-gray-600'
              ]"
            >
              {{ page }}
            </button>
          </nav>
        </div>
      </div>
    </main>

    <!-- Lightbox Modal -->
  <!-- Lightbox Modal - Update this section -->
  <transition name="fade">
    <div class="relative max-w-4xl w-full max-h-[90vh]">
  <!-- Navigation Arrows -->

<div v-if="lightboxOpen" class="fixed inset-0 z-[9999]  bg-black/90 backdrop-blur-lg flex items-center justify-center p-4" @click.self="closeLightbox">
  <button @click="closeLightbox" class="absolute top-6 right-6 text-white text-2xl z-[9999] hover:text-blue-400 transition-colors">✕</button>
  
  <div class="relative max-w-4xl w-full max-h-[90vh]">
    <!-- Image Viewer -->
    <img
      v-if="activeMedia.type === 'image'"
      :src="activeMedia.src"
      :alt="activeMedia.title"
      class="w-full h-full object-contain max-h-[80vh] mx-auto"
    />

    <!-- Video Player -->
    <video
      v-else-if="activeMedia.type === 'video'"
      :src="activeMedia.src"
      controls
      autoplay
      class="w-full max-h-[80vh] mx-auto"
    ></video>

    <!-- Media Info -->
    <div class="mt-6 text-white bg-gray-900/80 p-4 rounded-lg">
      <h3 class="text-2xl font-bold mb-2">Untitled</h3>
      <p class="text-gray-300 mb-4">Untitled.description</p>
      <div class="flex justify-between items-center">
        <span class="text-sm px-3 py-1 rounded-full bg-blue-500/20 text-blue-300">
          {{ getCategoryName(activeMedia.category) }}
        </span>
        <span class="text-sm text-gray-400">
          Some Day
          <!-- {{ formatDate(activeMedia.date) }} -->
        </span>
      </div>
    </div>
  </div>
</div>
</div>
</transition>

    
    <!-- Footer -->
    <footer class="w-full px-[12.5%] py-12 border-t border-gray-800 bg-gray-900/50">
      <div class="max-w-6xl mx-auto">
        <div class="flex flex-col md:flex-row justify-between items-center">
          <div class="mb-6 md:mb-0">
            <h2 class="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300 mb-2">
              My Gallery
            </h2>
            <p class="text-gray-400">Creativity with this page.</p>
          </div>
          <div class="flex gap-4">
            <a href="#" class="text-gray-400 hover:text-blue-300 transition-colors">
              <span class="sr-only">Twitter</span>
              <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
              </svg>
            </a>
            <a href="#" class="text-gray-400 hover:text-blue-300 transition-colors">
              <span class="sr-only">GitHub</span>
              <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path fill-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clip-rule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
        <div class="mt-8 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
          © 2025 My Gallery. All rights reserved.
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

// Categories with emojis
const categories = ref([
  { id: 'Abstract Painting', name: 'Abstract Painting', emoji: '🎨' },
  { id: 'Ai', name: 'Ai', emoji: '⚡' },
  { id: 'Digital Art', name: 'Digital Art', emoji: '🖥️' },
  { id: 'Textures', name: 'Textures', emoji: '🧵' },
  { id: 'Photography', name: 'Photography', emoji: '📸' },
])

const getImagePath = (baseName) => {
  try {
    return require(`@/assets/Gallery/${baseName}.jpeg`);
  } catch {
    try {
      return require(`@/assets/Gallery/${baseName}.jpg`);
    } catch {
      try {
        return require(`@/assets/Gallery/${baseName}.png`);
      } catch {
        return require('@/assets/Gallery/pho7.png');
      }
    }
  }
};
// Mock media data
const mediaItems = ref([
  // Helper function to handle image paths

  // AI Category (ai0-ai12)
  ...Array.from({ length: 41 }, (_, i) => ({
    id: `ai${i}`,
    title: ['Neural Dreams', 'AI Visions', 'Algorithmic Art', 'Machine Imagination'][Math.floor(Math.random() * 4)],
    description: ['Generated by deep learning', 'AI-created visual', 'Machine learning output', 'Computational artwork'][Math.floor(Math.random() * 4)],
    type: 'image',
    category: 'Ai',
    src: getImagePath(`ai${i}`),
    thumbnail: getImagePath(`ai${i}`),
    date: `2023-${Math.floor(Math.random() * 12) + 1}-${Math.floor(Math.random() * 28) + 1}`
  })),

  // Digital Art (dig0-dig5)
  ...Array.from({ length: 5 }, (_, i) => ({
    id: `dig${i}`,
    title: ['Digital Dreamscape', 'Cyber Dimensions', 'Pixel Universe'][Math.floor(Math.random() * 3)],
    description: ['3D rendered artwork', 'Digital painting', 'Vector illustration'][Math.floor(Math.random() * 3)],
    type: 'image',
    category: 'Digital Art',
    src: getImagePath(`dig${i}`),
    thumbnail: getImagePath(`dig${i}`),
    date: `2023-${Math.floor(Math.random() * 12) + 1}-${Math.floor(Math.random() * 28) + 1}`
  })),

  // Abstract Painting (paint0-paint12)
  ...Array.from({ length: 12 }, (_, i) => ({
    id: `paint${i}`,
    title: ['Emotional Waves', 'Color Symphony', 'Formless Beauty'][Math.floor(Math.random() * 3)],
    description: ['Acrylic composition', 'Mixed media artwork', 'Textural exploration'][Math.floor(Math.random() * 3)],
    type: 'image',
    category: 'Abstract Painting',
    src: getImagePath(`paint${i}`),
    thumbnail: getImagePath(`paint${i}`),
    date: `2023-${Math.floor(Math.random() * 12) + 1}-${Math.floor(Math.random() * 28) + 1}`
  })),


  // Additional Photography (pho0-pho7)
  ...Array.from({ length: 8 }, (_, i) => ({
    id: `pho${i}`,
    title: ['Silhouette Study', 'Golden Hour', 'Minimalist Frame'][Math.floor(Math.random() * 3)],
    description: ['Black and white study', 'Portrait photography', 'Detail shot'][Math.floor(Math.random() * 3)],
    type: 'image',
    category: 'Photography',
    src: getImagePath(`pho${i}`),
    thumbnail: getImagePath(`pho${i}`),
    date: `2023-${Math.floor(Math.random() * 12) + 1}-${Math.floor(Math.random() * 28) + 1}`
  })),

  // Textures (tex0-tex6)
  ...Array.from({ length: 7 }, (_, i) => ({
    id: `tex${i}`,
    title: ['Marble Veins', 'Concrete Surface', 'Fabric Texture'][Math.floor(Math.random() * 3)],
    description: ['Seamless texture', 'Material reference', 'Surface detail'][Math.floor(Math.random() * 3)],
    type: 'image',
    category: 'Textures',
    src: getImagePath(`tex${i}`),
    thumbnail: getImagePath(`tex${i}`),
    date: `2023-${Math.floor(Math.random() * 12) + 1}-${Math.floor(Math.random() * 28) + 1}`
  })),

]);


console.log(mediaItems.data)
// State
const searchQuery = ref('')
const activeCategory = ref(null)
const currentPage = ref(1)
const itemsPerPage = 9
const lightboxOpen = ref(false)
const activeMedia = ref(null)


function  switchPage(newPage) {
  currentPage.value = newPage; 
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
  })
}



// Computed properties
const filteredMedia = computed(() => {
  let filtered = mediaItems.value

  if (activeCategory.value) {
    filtered = filtered.filter(item => item.category === activeCategory.value)
  }

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(item => 
      item.title.toLowerCase().includes(query) || 
      item.description.toLowerCase().includes(query)
    )
  }

  return filtered
})

const totalPages = computed(() => {
  return Math.ceil(filteredMedia.value.length / itemsPerPage)
})

const paginatedMedia = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return filteredMedia.value.slice(start, end)
})

// Methods
function setActiveCategory(category) {
  activeCategory.value = activeCategory.value === category ? null : category
  currentPage.value = 1
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  })
}

function getCategoryName(categoryId) {
  const category = categories.value.find(c => c.id === categoryId)
  return category ? category.name : 'Uncategorized'
}

// Handle keyboard events for lightbox
const handleKeyDown = (e) => {
  if (lightboxOpen.value) {
    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowRight') {
      navigateLightbox(1);
    } else if (e.key === 'ArrowLeft') {
      navigateLightbox(-1);
    }
  }
};

// Navigation between items in lightbox
const navigateLightbox = (direction) => {
  if (!activeMedia.value) return;
  
  const currentIndex = filteredMedia.value.findIndex(item => item.id === activeMedia.value.id);
  const newIndex = (currentIndex + direction + filteredMedia.value.length) % filteredMedia.value.length;
  
  activeMedia.value = filteredMedia.value[newIndex];
};


onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
});

function openLightbox(mediaItem) {
  activeMedia.value = mediaItem
  lightboxOpen.value = true
  
  // Pause all videos when lightbox opens
  document.querySelectorAll('video').forEach(video => {
    video.pause()
  })
}

function closeLightbox() {
  lightboxOpen.value = false
  activeMedia.value = null
}
</script>

<style>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out forwards;
}

.backdrop-blur-sm {
  backdrop-filter: blur(8px);
}

.bg-gray-800 {
  background-color: rgba(31, 41, 55, 0.5);
}

/* Custom scrollbar for lightbox */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.lightbox {
  position: fixed;
  z-index: 9999;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Lightbox transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scale-enter-active,
.scale-leave-active {
  transition: all 0.3s ease;
}

.scale-enter-from,
.scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>